import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, MapPin, Loader2, ArrowLeft, ArrowRight, Trash2, CheckCircle2, Sparkles, Pencil } from "lucide-react";
import { toast } from "sonner";
import { PhotoUpload } from "@/components/PhotoUpload";
import { CLEANING_COMPANIES, REFERENTS, PLATFORM_META, KEY_HANDOVER, CHECKOUT_METHODS } from "@/lib/constants";
import { useCleaningCompanies } from "@/lib/useCleaningCompanies";

const STEPS = ["Général", "Caractéristiques", "Accès", "Photos", "Plateformes", "Ménage", "Validation"];

const IDF = {
  "75": "Paris (75)", "77": "Seine-et-Marne (77)", "78": "Yvelines (78)",
  "91": "Essonne (91)", "92": "Hauts-de-Seine (92)", "93": "Seine-Saint-Denis (93)",
  "94": "Val-de-Marne (94)", "95": "Val-d'Oise (95)",
};

const initialForm = {
  name: "", address: "", postal_code: "", city: "", arrondissement: "",
  department: "", region: "", country: "France",
  latitude: null, longitude: null,
  floor: "", elevator: false, surface_m2: "", rooms: "", bedrooms: "", bathrooms: "", wc: "",
  max_guests: 2, sleepings: 2, beds_double: 0, beds_single: 0, beds_sofa: 0,
  mobility_lease: false, key_at_office: false, wifi_available: false, wifi_name: "", wifi_password: "",
  ac_available: false, ac_type: null,
  status: "Onboarding",
  building_code: "", intercom: "", door_position: "",
  keybox: false, keybox_code: "", keynest: false, keynest_address: "", keynest_ref: "",
  checkout_methods: [], checkout_keynest_code: "",
  key_office_box: "",
  vigik: "", keys_count: 1, second_key_location: "", gate_code: "", parking_code: "",
  photo_url: null, access_steps: [], platforms: [],
  cleaning_company: "Non renseigné", referent: "Dylan", owner_name: "", owner_email: "", drive_folder_id: "",
};

function MiniMap({ lat, lng, onMove }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    const map = L.map(ref.current, { zoomControl: false }).setView([lat, lng], 16);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "© OSM", maxZoom: 19 }).addTo(map);
    const pin = L.divIcon({
      className: "",
      html: '<div style="position:relative"><div style="width:18px;height:18px;background:#2563EB;border:3px solid #fff;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,.4)"></div><div style="position:absolute;left:50%;top:16px;transform:translateX(-50%);width:2px;height:10px;background:#2563EB;border-radius:1px"></div></div>',
      iconSize: [18, 28],
      iconAnchor: [9, 9],
    });
    const marker = L.marker([lat, lng], { draggable: true, icon: pin }).addTo(map);
    marker.on("dragend", () => {
      const p = marker.getLatLng();
      onMove(p.lat, p.lng);
    });
    map.on("click", (e) => {
      marker.setLatLng(e.latlng);
      onMove(e.latlng.lat, e.latlng.lng);
    });
    mapRef.current = map;
    markerRef.current = marker;
  }, []);
  useEffect(() => {
    if (markerRef.current && mapRef.current) {
      markerRef.current.setLatLng([lat, lng]);
      mapRef.current.setView([lat, lng], Math.max(mapRef.current.getZoom(), 15));
    }
  }, [lat, lng]);
  return <div ref={ref} className="h-56 rounded-xl border border-slate-200 overflow-hidden" data-testid="wizard-minimap" />;
}

function Field({ label, children }) {
  return <div><Label className="text-slate-600">{label}</Label><div className="mt-1">{children}</div></div>;
}

export default function ApartmentWizard({ onAdded, onSaved, apartment = null }) {
  const done = onSaved || onAdded;
  const isEdit = !!apartment;
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(() => (isEdit ? { ...initialForm, ...apartment } : initialForm));
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState("");
  const { names: cleaningCompanyNames } = useCleaningCompanies();
  const companyOptions = [...new Set([...cleaningCompanyNames, form.cleaning_company, "Non renseigné"].filter(Boolean))];
  const [manualMode, setManualMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [smartText, setSmartText] = useState("");
  const [smartLoading, setSmartLoading] = useState(false);
  const [smartInfo, setSmartInfo] = useState(null);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const isT2 = parseInt(form.bedrooms) === 1;
  const t2Valid = !isT2 ||
    (parseInt(form.rooms) === 2 && (parseInt(form.beds_double) || 0) >= 1 && (parseInt(form.beds_sofa) || 0) >= 1);

  const setBedrooms = (v) => {
    const patch = { bedrooms: v };
    if (parseInt(v) === 1) {
      patch.rooms = "2";
      if (!(parseInt(form.beds_double) >= 1)) patch.beds_double = 1;
      if (!(parseInt(form.beds_sofa) >= 1)) patch.beds_sofa = 1;
    }
    set(patch);
  };

  const smartFill = async () => {
    if (!smartText.trim()) return;
    setSmartLoading(true); setSmartInfo(null);
    try {
      const { data } = await api.post("/apartments/parse", { text: smartText });
      const f = data.fields || {};
      const patch = {};
      for (const k of Object.keys(initialForm)) {
        if (f[k] !== undefined && f[k] !== null && f[k] !== "") patch[k] = f[k];
      }
      if (f.platforms?.length) patch.platforms = f.platforms;
      if (f.keynest) patch.key_handover = "KeyNest";
      else if (f.keybox) patch.key_handover = "Boîte à clés";
      set(patch);
      if (parseInt(patch.bedrooms ?? form.bedrooms) === 1) {
        const t2 = { rooms: "2" };
        const cur = { ...form, ...patch };
        if (!(parseInt(cur.beds_double) >= 1)) t2.beds_double = 1;
        if (!(parseInt(cur.beds_sofa) >= 1)) t2.beds_sofa = 1;
        set(t2);
      }
      setSmartInfo({ filled: data.filled_count, missing: data.missing || [] });
      const addr = f.address || form.address;
      const cp = f.postal_code || form.postal_code;
      if (addr && cp) setTimeout(() => geocode(addr, cp), 300);
    } catch (e) {
      toast.error("Analyse impossible — remplissez manuellement");
    } finally {
      setSmartLoading(false);
    }
  };

  const geocode = async (addr, cp) => {
    const cleanAddr = String(typeof addr === "string" ? addr : form.address ?? "")
      .replace(/[,;]?\s*(code|bo[îi]te|coffre|digicode)[^,;]*/gi, "").trim();
    const postal = String(typeof cp === "string" ? cp : form.postal_code ?? "").trim();
    if (!cleanAddr) return;
    setGeoLoading(true); setGeoError(""); setManualMode(false);
    try {
      const params = new URLSearchParams({
        format: "json", addressdetails: "1", limit: "5", countrycodes: "fr,be,mc,es",
      });
      params.set("street", cleanAddr);
      if (postal) params.set("postalcode", postal);
      const r = await fetch(`https://nominatim.openstreetmap.org/search?${params}`,
        { headers: { "Accept-Language": "fr" } });
      let data = await r.json();
      if (!data.length) {
        const p2 = new URLSearchParams({
          format: "json", addressdetails: "1", limit: "5", countrycodes: "fr,be,mc,es",
          q: `${cleanAddr} ${postal}`.trim(),
        });
        const r2 = await fetch(`https://nominatim.openstreetmap.org/search?${p2}`,
          { headers: { "Accept-Language": "fr" } });
        data = await r2.json();
      }
      if (postal && data.length > 1) {
        const exact = data.find((g) => g.address?.postcode === postal);
        if (exact) data = [exact];
      }
      if (!data.length) {
        setGeoError("Nous n'avons pas pu localiser précisément cette adresse.");
        return;
      }
      const g = data[0];
      const ad = g.address || {};
      const postcode = ad.postcode || postal || form.postal_code;
      if (postal && ad.postcode && ad.postcode !== postal) {
        setGeoError(`Adresse localisée mais le code postal trouvé (${ad.postcode}) diffère de celui saisi (${postal}) — vérifiez le point sur la carte.`);
      }
      const deptCode = (postcode || "").slice(0, 2);
      let arrondissement = form.arrondissement;
      if (/^750\d{2}$/.test(postcode)) {
        const n = parseInt(postcode.slice(2), 10);
        arrondissement = `${n}${n === 1 ? "er" : "e"}`;
      }
      set({
        latitude: parseFloat(g.lat), longitude: parseFloat(g.lon),
        city: ad.city || ad.town || ad.village || form.city,
        postal_code: postcode || form.postal_code,
        arrondissement,
        department: IDF[deptCode] || ad.county || form.department,
        region: ad.state || form.region,
        country: ad.country || "France",
      });
    } catch {
      setGeoError("Nous n'avons pas pu localiser précisément cette adresse.");
    } finally {
      setGeoLoading(false);
    }
  };

  const canNext = () => {
    if (step === 0) return form.name && form.address && form.latitude != null;
    if (step === 1) return t2Valid;
    if (step === 2) {
      if (!form.key_handover) return false;
      if (form.key_handover === "Boîte à clés" && !(form.keybox_code || "").trim()) return false;
      if (form.key_handover === "KeyNest" &&
          !((form.keynest_address || "").trim() && /^\d{8}$/.test((form.keynest_ref || "").trim()))) return false;
      if (!(form.checkout_methods || []).length) return false;
      if (form.checkout_methods.includes("KeyNest") && !/^\d{8}$/.test((form.checkout_keynest_code || "").trim())) return false;
      return true;
    }
    return true;
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        surface_m2: form.surface_m2 ? parseFloat(form.surface_m2) : null,
        rooms: form.rooms ? parseInt(form.rooms) : null,
        bedrooms: form.bedrooms ? parseInt(form.bedrooms) : null,
        bathrooms: form.bathrooms ? parseInt(form.bathrooms) : null,
        wc: form.wc ? parseInt(form.wc) : null,
        max_guests: parseInt(form.max_guests) || 2,
        sleepings: parseInt(form.sleepings) || 2,
        keys_count: parseInt(form.keys_count) || null,
        access_steps: form.access_steps.map((s, i) => ({ ...s, order: i + 1 })),
        keybox: form.key_handover === "Boîte à clés",
        keynest: form.key_handover === "KeyNest",
      };
      delete payload.id; delete payload.color_status; delete payload.open_problems;
      delete payload.created_at; delete payload.updated_at;
      if (isEdit) {
        await api.put(`/apartments/${apartment.id}`, payload);
        toast.success("Appartement mis à jour");
      } else {
        await api.post("/apartments", payload);
        toast.success("Appartement créé (statut : Onboarding)");
      }
      setOpen(false);
      if (!isEdit) {
        setStep(0); setForm(initialForm); setGeoError(""); setManualMode(false);
        setSmartText(""); setSmartInfo(null);
      }
      done?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const located = form.latitude != null && form.longitude != null;

  const stepBody = [
    // 1 — Général
    <div className="space-y-3" key="s1">
      <div className="border border-sky-200 bg-sky-50/60 rounded-xl p-3 space-y-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <Sparkles className="w-4 h-4 text-sky-600" /> Remplissage intelligent
        </div>
        <Textarea value={smartText} onChange={(e) => setSmartText(e.target.value)} rows={4}
                  placeholder="Collez ici toutes les informations de l'appartement en texte libre (adresse, codes, Wi-Fi, capacité, ménage…) — l'IA remplit le formulaire automatiquement."
                  data-testid="smart-fill-text" className="bg-white text-sm" />
        <Button type="button" size="sm" onClick={smartFill} disabled={smartLoading || !smartText.trim()}
                data-testid="smart-fill-btn" className="bg-slate-900 hover:bg-slate-800 text-white">
          {smartLoading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
          Analyser et remplir
        </Button>
        {smartInfo && (
          <div className="space-y-1.5">
            <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5" data-testid="smart-filled">
              {smartInfo.filled} champ(s) rempli(s) automatiquement
            </div>
            {smartInfo.missing.length > 0 && (
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5" data-testid="smart-missing">
                Informations manquantes à compléter manuellement : {smartInfo.missing.join(", ")}
              </div>
            )}
          </div>
        )}
      </div>
      <Field label="Nom interne *">
        <Input value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="Ex : Montmartre – Ruisseau" data-testid="wz-name" />
      </Field>
      <Field label="Adresse *">
        <Input value={form.address} onChange={(e) => set({ address: e.target.value })} placeholder="12 rue du Ruisseau" data-testid="wz-address" />
      </Field>
      <Field label="Code postal *">
        <Input value={form.postal_code} onChange={(e) => set({ postal_code: e.target.value })} placeholder="75018" data-testid="wz-postal" />
      </Field>
      <Button type="button" variant="outline" onClick={() => geocode()} disabled={geoLoading || !form.address}
              data-testid="wz-geocode" className="w-full">
        {geoLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MapPin className="w-4 h-4 mr-2" />}
        Localiser automatiquement
      </Button>
      {geoError && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg p-3" data-testid="wz-geo-error">
          {geoError}
          <div className="flex gap-2 mt-2">
            <Button size="sm" variant="outline" onClick={() => { setManualMode(true); set({ latitude: 48.8566, longitude: 2.3522 }); }} data-testid="wz-manual-pos">
              Positionner manuellement
            </Button>
          </div>
        </div>
      )}
      {located && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1.5" data-testid="wz-detected">
            {form.city && <Badge variant="outline">{form.city}</Badge>}
            {form.arrondissement && <Badge variant="outline">{form.arrondissement} arr.</Badge>}
            {form.department && <Badge variant="outline">{form.department}</Badge>}
            {form.region && <Badge variant="outline">{form.region}</Badge>}
            <Badge variant="outline">{form.country}</Badge>
          </div>
          <MiniMap lat={form.latitude} lng={form.longitude} onMove={(lat, lng) => set({ latitude: lat, longitude: lng })} />
          <p className="text-xs text-slate-500">Déplacez le marqueur ou cliquez sur la carte si la position n'est pas exacte.</p>
        </div>
      )}
    </div>,
    // 2 — Caractéristiques
    <div className="space-y-3" key="s2">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Étage"><Input value={form.floor} onChange={(e) => set({ floor: e.target.value })} placeholder="3e" data-testid="wz-floor" /></Field>
        <Field label="Surface (m²)"><Input type="number" value={form.surface_m2} onChange={(e) => set({ surface_m2: e.target.value })} /></Field>
        <Field label="Pièces"><Input type="number" value={form.rooms} onChange={(e) => set({ rooms: e.target.value })} data-testid="wz-rooms" /></Field>
        <Field label="Chambres"><Input type="number" value={form.bedrooms} onChange={(e) => setBedrooms(e.target.value)} data-testid="wz-bedrooms" /></Field>
        <Field label="Salles de bain"><Input type="number" value={form.bathrooms} onChange={(e) => set({ bathrooms: e.target.value })} /></Field>
        <Field label="WC"><Input type="number" value={form.wc} onChange={(e) => set({ wc: e.target.value })} /></Field>
        <Field label="Capacité max"><Input type="number" value={form.max_guests} onChange={(e) => set({ max_guests: e.target.value })} data-testid="wz-capacity" /></Field>
        <Field label="Couchages"><Input type="number" value={form.sleepings} onChange={(e) => set({ sleepings: e.target.value })} /></Field>
        <Field label="Lits doubles"><Input type="number" value={form.beds_double} onChange={(e) => set({ beds_double: parseInt(e.target.value) || 0 })} data-testid="wz-beds-double" /></Field>
        <Field label="Lits simples"><Input type="number" value={form.beds_single} onChange={(e) => set({ beds_single: parseInt(e.target.value) || 0 })} /></Field>
        <Field label="Canapés-lits"><Input type="number" value={form.beds_sofa} onChange={(e) => set({ beds_sofa: parseInt(e.target.value) || 0 })} data-testid="wz-beds-sofa" /></Field>
      </div>
      {isT2 && (
        <div className={`text-xs rounded-lg px-2.5 py-1.5 border ${t2Valid ? "text-sky-700 bg-sky-50 border-sky-200" : "text-red-700 bg-red-50 border-red-200"}`}
             data-testid="wz-t2-rule">
          1 chambre = T2 : 2 pièces, au moins 1 lit double et 1 canapé-lit sont obligatoires.
        </div>
      )}
      <div className="flex flex-wrap gap-x-6 gap-y-3 pt-1">
        <label className="flex items-center gap-2 text-sm"><Switch checked={form.elevator} onCheckedChange={(v) => set({ elevator: v })} data-testid="wz-elevator" /> Ascenseur</label>
        <label className="flex items-center gap-2 text-sm"><Switch checked={form.wifi_available} onCheckedChange={(v) => set({ wifi_available: v })} data-testid="wz-wifi" /> Wi-Fi</label>
        <label className="flex items-center gap-2 text-sm"><Switch checked={form.ac_available} onCheckedChange={(v) => set({ ac_available: v, ac_type: v ? (form.ac_type || "Fixe") : null })} data-testid="wz-ac" /> Climatisation</label>
        <label className="flex items-center gap-2 text-sm"><Switch checked={form.mobility_lease} onCheckedChange={(v) => set({ mobility_lease: v })} data-testid="wz-mobility" /> Bail mobilité</label>
        <label className="flex items-center gap-2 text-sm"><Switch checked={form.key_at_office} onCheckedChange={(v) => set({ key_at_office: v })} data-testid="wz-key-office" /> Clé au bureau</label>
      </div>
      {form.key_at_office && (
        <Field label="Numéro de boîte (clé au bureau)">
          <Input value={form.key_office_box || ""} onChange={(e) => set({ key_office_box: e.target.value })}
                 placeholder="Ex. boîte n° 12" data-testid="wz-key-office-box" />
        </Field>
      )}
      {form.mobility_lease && (
        <Field label="ID dossier Drive (obligatoire pour créer/envoyer un bail)">
          <Input value={form.drive_folder_id || ""} onChange={(e) => set({ drive_folder_id: e.target.value })}
                 placeholder="Collez le lien du dossier Drive ou son ID" data-testid="wz-drive-id" />
          <p className="text-[11px] text-red-500 mt-1">Sans ID, l'appartement sera bloqué « ID Drive en attente » dans Gestion des BM.</p>
        </Field>
      )}
      {form.ac_available && (
        <Field label="Type de climatisation">
          <Select value={form.ac_type || "Fixe"} onValueChange={(v) => set({ ac_type: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="Fixe">Fixe</SelectItem><SelectItem value="Mobile">Mobile</SelectItem></SelectContent>
          </Select>
        </Field>
      )}
      {form.wifi_available && (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Réseau Wi-Fi"><Input value={form.wifi_name} onChange={(e) => set({ wifi_name: e.target.value })} /></Field>
          <Field label="Mot de passe Wi-Fi"><Input value={form.wifi_password} onChange={(e) => set({ wifi_password: e.target.value })} /></Field>
        </div>
      )}
    </div>,
    // 3 — Accès
    <div className="space-y-3" key="s3">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Code immeuble"><Input value={form.building_code} onChange={(e) => set({ building_code: e.target.value })} data-testid="wz-building-code" /></Field>
        <Field label="Interphone"><Input value={form.intercom} onChange={(e) => set({ intercom: e.target.value })} /></Field>
        <Field label="Position / porte"><Input value={form.door_position} onChange={(e) => set({ door_position: e.target.value })} placeholder="1ère à droite" /></Field>
        <Field label="Badge / Vigik"><Input value={form.vigik} onChange={(e) => set({ vigik: e.target.value })} /></Field>
        <Field label="Code portail"><Input value={form.gate_code} onChange={(e) => set({ gate_code: e.target.value })} /></Field>
        <Field label="Code parking"><Input value={form.parking_code} onChange={(e) => set({ parking_code: e.target.value })} /></Field>
        <Field label="Nombre de clés"><Input type="number" value={form.keys_count} onChange={(e) => set({ keys_count: e.target.value })} /></Field>
        <Field label="Emplacement 2ᵉ clé"><Input value={form.second_key_location} onChange={(e) => set({ second_key_location: e.target.value })} /></Field>
      </div>
      <Field label="Remise des clés *">
        <Select value={form.key_handover || ""} onValueChange={(v) => set({ key_handover: v })}>
          <SelectTrigger data-testid="wz-key-handover"><SelectValue placeholder="Choisir le mode de remise…" /></SelectTrigger>
          <SelectContent>
            {KEY_HANDOVER.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      {form.key_handover === "Boîte à clés" && (
        <Field label="Code boîte à clés *"><Input value={form.keybox_code || ""} onChange={(e) => set({ keybox_code: e.target.value })} data-testid="wz-keybox-code" /></Field>
      )}
      {form.key_handover === "KeyNest" && (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Adresse KeyNest *"><Input value={form.keynest_address || ""} onChange={(e) => set({ keynest_address: e.target.value })} data-testid="wz-keynest-address" /></Field>
          <Field label="Code de retrait KeyNest * (8 chiffres)"><Input value={form.keynest_ref || ""} onChange={(e) => set({ keynest_ref: e.target.value.replace(/\D/g, "").slice(0, 8) })} inputMode="numeric" maxLength={8} placeholder="12345678" data-testid="wz-keynest-ref" /></Field>
        </div>
      )}
      <div className="border-t border-slate-100 pt-3">
        <Field label="Restitution des clés au check-out *">
          <div className="flex flex-wrap gap-2" data-testid="wz-checkout-methods">
            {CHECKOUT_METHODS.map((m) => {
              const on = form.checkout_methods.includes(m);
              return (
                <button type="button" key={m}
                        onClick={() => set({ checkout_methods: on ? form.checkout_methods.filter((x) => x !== m) : [...form.checkout_methods, m] })}
                        data-testid={`wz-checkout-${m}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                          on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}>
                  {m}
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-slate-400 mt-1">Plusieurs choix possibles.</p>
        </Field>
        {form.checkout_methods.includes("Boîte à clés") && (
          <p className="text-xs text-slate-500 mt-2">
            Le client redépose les clés dans la boîte à clés (même code qu'à l'arrivée{form.keybox_code ? ` : ${form.keybox_code}` : ""}).
          </p>
        )}
        {form.checkout_methods.includes("KeyNest") && (
          <div className="mt-2">
            <p className="text-xs text-slate-500 mb-2">
              Le dépôt se fait au même point KeyNest que le retrait{form.keynest_address ? ` (${form.keynest_address})` : " (adresse indiquée à la remise des clés)"}.
            </p>
            <Field label="Code dépôt KeyNest * (8 chiffres)"><Input value={form.checkout_keynest_code || ""} onChange={(e) => set({ checkout_keynest_code: e.target.value.replace(/\D/g, "").slice(0, 8) })} inputMode="numeric" maxLength={8} placeholder="12345678" data-testid="wz-checkout-keynest-code" /></Field>
          </div>
        )}
      </div>
      <div className="border-t border-slate-100 pt-3">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-slate-600">Étapes d'accès (guide photo)</Label>
          <Button type="button" size="sm" variant="outline"
                  onClick={() => set({ access_steps: [...form.access_steps, { id: crypto.randomUUID(), order: form.access_steps.length + 1, title: `Étape ${form.access_steps.length + 1}`, description: "", photo_url: null }] })}
                  data-testid="wz-add-step">
            <Plus className="w-3.5 h-3.5 mr-1" />Étape
          </Button>
        </div>
        <div className="space-y-2">
          {form.access_steps.map((s, i) => (
            <div key={s.id} className="border border-slate-200 rounded-lg p-2.5 space-y-2">
              <div className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-slate-900 text-white flex items-center justify-center text-[11px] font-bold flex-shrink-0">{i + 1}</span>
                <Input value={s.title} className="h-8" placeholder="Titre"
                       onChange={(e) => set({ access_steps: form.access_steps.map((x, j) => j === i ? { ...x, title: e.target.value } : x) })}
                       data-testid={`wz-step-title-${i}`} />
                <button type="button" onClick={() => set({ access_steps: form.access_steps.filter((_, j) => j !== i) })}
                        className="text-red-400 hover:text-red-600" data-testid={`wz-step-del-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <Textarea value={s.description} rows={2} placeholder="Description"
                        onChange={(e) => set({ access_steps: form.access_steps.map((x, j) => j === i ? { ...x, description: e.target.value } : x) })} />
              <PhotoUpload value={s.photo_url}
                           onChange={(url) => set({ access_steps: form.access_steps.map((x, j) => j === i ? { ...x, photo_url: url } : x) })}
                           testid={`wz-step-photo-${i}`} />
            </div>
          ))}
        </div>
      </div>
    </div>,
    // 4 — Photos
    <div className="space-y-3" key="s4">
      <Field label="Photo principale de l'appartement">
        <PhotoUpload value={form.photo_url} onChange={(url) => set({ photo_url: url })} label="Téléverser la photo principale" testid="wz-main-photo" />
      </Field>
      <p className="text-xs text-slate-500">Depuis un ordinateur ou un téléphone (appareil photo ou galerie). Aperçu avant validation.</p>
    </div>,
    // 5 — Plateformes
    <div className="space-y-3" key="s5">
      {form.platforms.map((p, i) => (
        <div key={i} className="border border-slate-200 rounded-lg p-3 flex items-end gap-2">
          <div className="flex-1 grid grid-cols-3 gap-2">
            <Select value={p.platform} onValueChange={(v) => set({ platforms: form.platforms.map((x, j) => j === i ? { ...x, platform: v } : x) })}>
              <SelectTrigger data-testid={`wz-platform-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(PLATFORM_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input value={p.url} placeholder="Lien de l'annonce" className="col-span-2"
                   onChange={(e) => set({ platforms: form.platforms.map((x, j) => j === i ? { ...x, url: e.target.value } : x) })} />
          </div>
          <button type="button" onClick={() => set({ platforms: form.platforms.filter((_, j) => j !== i) })}
                  className="p-2 text-red-400 hover:text-red-600">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
      <Button type="button" variant="outline" className="w-full"
              onClick={() => set({ platforms: [...form.platforms, { platform: "airbnb", url: "", external_id: "" }] })}
              data-testid="wz-add-platform">
        <Plus className="w-4 h-4 mr-1.5" />Ajouter une plateforme
      </Button>
    </div>,
    // 6 — Ménage
    <div className="space-y-3" key="s6">
      <Field label="Entreprise de ménage">
        <Select value={form.cleaning_company} onValueChange={(v) => set({ cleaning_company: v })}>
          <SelectTrigger data-testid="wz-cleaning"><SelectValue /></SelectTrigger>
          <SelectContent>{companyOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Référent">
        <Select value={form.referent} onValueChange={(v) => set({ referent: v })}>
          <SelectTrigger data-testid="wz-referent"><SelectValue /></SelectTrigger>
          <SelectContent>{REFERENTS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Propriétaire">
        <Input value={form.owner_name} onChange={(e) => set({ owner_name: e.target.value })} data-testid="wz-owner" placeholder="Nom et prénom" />
      </Field>
      <Field label="Email du propriétaire (pour le bail)">
        <Input type="email" value={form.owner_email || ""} onChange={(e) => set({ owner_email: e.target.value })} data-testid="wz-owner-email" />
      </Field>
    </div>,
    // 7 — Validation
    <div className="space-y-2" key="s7">
      <div className="bg-slate-50 rounded-xl p-4 space-y-1.5 text-sm" data-testid="wz-summary">
        {[
          ["Nom", form.name],
          ["Adresse", form.address],
          ["Localisation", [form.city, form.arrondissement && `${form.arrondissement} arr.`, form.department, form.region, form.country].filter(Boolean).join(" · ")],
          ["Statut", form.status],
          ["Capacité", `${form.max_guests} voyageurs · ${form.sleepings} couchages`],
          ["Wi-Fi", form.wifi_available ? "Oui" : "Non"],
          ["Climatisation", form.ac_available ? (form.ac_type || "Oui") : "Non"],
          ["Bail mobilité", form.mobility_lease ? "Oui" : "Non"],
          ["Clé au bureau", form.key_at_office ? "Oui" : "Non"],
          ["Ménage", form.cleaning_company],
          ["Référent", form.referent],
          ["Plateformes", form.platforms.length ? form.platforms.map((p) => PLATFORM_META[p.platform]?.name).join(", ") : "Aucune"],
          ["Étapes d'accès", String(form.access_steps.length)],
          ["Restitution clés (check-out)", form.checkout_methods.length ? form.checkout_methods.join(", ") : ""],
          ["Photo principale", form.photo_url ? "Oui" : "Non"],
        ].map(([k, v]) => (
          <div key={k} className="flex justify-between gap-4">
            <span className="text-slate-500">{k}</span>
            <span className="text-slate-900 font-medium text-right">{v || "—"}</span>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
        Le logement sera créé avec le statut Onboarding. Il pourra passer "En ligne" une fois la configuration terminée.
      </div>
    </div>,
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setStep(0); } }}>
      <DialogTrigger asChild>
        {isEdit ? (
          <Button variant="outline" size="sm" data-testid="edit-apt-btn">
            <Pencil className="w-4 h-4 mr-1.5" />Modifier
          </Button>
        ) : (
          <Button data-testid="add-apt-btn" className="bg-slate-900 hover:bg-slate-800 text-white">
            <Plus className="w-4 h-4 mr-1.5" />Ajouter un appartement
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[92vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>{isEdit ? "Modifier l'appartement" : "Nouvel appartement"}</span>
            <span className="text-sm font-normal text-slate-500" data-testid="wz-step-indicator">Étape {step + 1} sur 7 — {STEPS[step]}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="h-1 bg-slate-100 rounded-full overflow-hidden mb-2">
          <div className="h-full bg-slate-900 rounded-full transition-all duration-300" style={{ width: `${((step + 1) / 7) * 100}%` }} />
        </div>
        <div className="flex-1 overflow-y-auto thin-scroll pr-1">{stepBody[step]}</div>
        <div className="flex justify-between pt-3 border-t border-slate-100">
          <Button variant="outline" onClick={() => setStep((s) => s - 1)} disabled={step === 0} data-testid="wz-prev">
            <ArrowLeft className="w-4 h-4 mr-1.5" />Précédent
          </Button>
          {step < 6 ? (
            <Button onClick={() => setStep((s) => s + 1)} disabled={!canNext()} data-testid="wz-next"
                    className="bg-slate-900 hover:bg-slate-800 text-white">
              Suivant<ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          ) : (
            <Button onClick={submit} disabled={saving} data-testid="wz-create"
                    className="bg-slate-900 hover:bg-slate-800 text-white">
              {saving ? "Enregistrement…" : isEdit ? "Enregistrer les modifications" : "Créer l'appartement"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
